// FASE 2.2 — Trading Journal page (/shadow/journal). PRO only.
// PnL %, win-rate, equity curve. Inline edit/close existing entries.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Loader2, Trash2, Crown, BookText, TrendingUp, TrendingDown, X as XIcon } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { API, safeJson } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { startProCheckout } from "../components/shadow/ProGate";
import { useLanguage } from "../i18n/LanguageContext";
import { tFases } from "../i18n/shadow-fases";

const SIDES = [
  { id: "LONG", color: "#10b981", icon: TrendingUp },
  { id: "SHORT", color: "#ef4444", icon: TrendingDown },
];

export default function ShadowJournalPage() {
  const { user, token, openLogin, isPro } = useAuth();
  const { lang } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingExit, setEditingExit] = useState("");
  const [editingLesson, setEditingLesson] = useState("");
  const [proBusy, setProBusy] = useState(false);

  const [form, setForm] = useState({
    ticker: "BTC", side: "LONG", entry_price: "", exit_price: "",
    position_size_usd: "", notes: "", lesson: "",
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!user || !isPro) { if (alive) setLoading(false); return; }
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [e, s] = await Promise.all([
          fetch(`${API}/shadow/journal`, { credentials: "include", headers }).then(safeJson),
          fetch(`${API}/shadow/journal/stats`, { credentials: "include", headers }).then(safeJson),
        ]);
        if (!alive) return;
        if (e?.entries) setEntries(e.entries);
        if (s) setStats(s);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [user, token, isPro]);

  const refresh = async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const [e, s] = await Promise.all([
      fetch(`${API}/shadow/journal`, { credentials: "include", headers }).then(safeJson),
      fetch(`${API}/shadow/journal/stats`, { credentials: "include", headers }).then(safeJson),
    ]);
    if (e?.entries) setEntries(e.entries);
    if (s) setStats(s);
  };

  const create = async (e) => {
    e?.preventDefault?.();
    if (creating) return;
    setError(null);
    const entry = parseFloat(form.entry_price);
    if (!isFinite(entry) || entry <= 0) { setError(tFases(lang, "j_f_exit_invalid")); return; }
    setCreating(true);
    try {
      const body = {
        ticker: form.ticker, side: form.side,
        entry_price: entry,
        exit_price: form.exit_price ? parseFloat(form.exit_price) : null,
        position_size_usd: form.position_size_usd ? parseFloat(form.position_size_usd) : null,
        notes: form.notes, lesson: form.lesson,
      };
      const res = await fetch(`${API}/shadow/journal`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await safeJson(res);
      if (!res.ok) {
        const msg = data?.detail?.message || data?.detail || "Error";
        throw new Error(typeof msg === "string" ? msg : "Error");
      }
      setShowForm(false);
      setForm({ ticker: "BTC", side: "LONG", entry_price: "", exit_price: "", position_size_usd: "", notes: "", lesson: "" });
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const closePosition = async (id) => {
    const exit_ = parseFloat(editingExit);
    if (!isFinite(exit_) || exit_ <= 0) return;
    try {
      const res = await fetch(`${API}/shadow/journal/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ exit_price: exit_, lesson: editingLesson || null }),
      });
      if (res.ok) {
        setEditingId(null);
        setEditingExit("");
        setEditingLesson("");
        await refresh();
      }
    } catch (_e) { /* non-critical */ }
  };

  const remove = async (id) => {
    if (!window.confirm(tFases(lang, "j_confirm_delete"))) return;
    try {
      const res = await fetch(`${API}/shadow/journal/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) await refresh();
    } catch (_e) { /* non-critical */ }
  };

  // Equity curve as cumulative pnl_pct
  const equityCurve = useMemo(() => {
    const closed = entries.filter((e) => e.pnl_pct !== null && e.pnl_pct !== undefined).reverse();
    let acc = 0;
    return closed.map((e) => { acc += Number(e.pnl_pct) || 0; return acc; });
  }, [entries]);

  const handleProUpgrade = useCallback(async () => {
    if (proBusy) return;
    setProBusy(true);
    try {
      await startProCheckout();
    } catch (_e) {
      setProBusy(false);
    }
  }, [proBusy]);

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar />
        <section className="mx-auto max-w-2xl px-6 pt-40 pb-24 text-center">
          <BookText className="mx-auto h-10 w-10 text-cyan-300/80" />
          <h1 className="mt-4 text-3xl font-black tracking-tight">{tFases(lang, "j_title")}</h1>
          <p className="mt-2 text-sm text-white/55">{tFases(lang, "j_login_sub")}</p>
          <button onClick={openLogin} className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-6 py-3 text-xs font-black uppercase tracking-[0.22em] text-black hover:-translate-y-0.5 transition-all" data-testid="journal-login-btn">
            {tFases(lang, "common_login")}
          </button>
        </section>
        <Footer />
      </div>
    );
  }

  if (!isPro) {
    return (
      <div className="min-h-screen bg-black text-white" data-testid="journal-pro-gate">
        <Navbar />
        <section className="mx-auto max-w-2xl px-6 pt-40 pb-24 text-center">
          <Crown className="mx-auto h-10 w-10 text-amber-300" />
          <h1 className="mt-4 text-3xl font-black tracking-tight">{tFases(lang, "j_pro_title")}</h1>
          <p className="mt-2 text-sm text-white/55">
            {tFases(lang, "j_pro_sub")}
          </p>
          <button
            onClick={handleProUpgrade}
            disabled={proBusy}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-3 text-xs font-black uppercase tracking-[0.22em] text-black shadow-[0_8px_24px_rgba(255,140,0,0.45)] disabled:cursor-wait disabled:opacity-60"
            data-testid="journal-pro-cta"
          >
            <Crown className="h-4 w-4" /> {proBusy ? tFases(lang, "j_pro_busy") : tFases(lang, "j_pro_cta")}
          </button>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="shadow-journal-page">
      <Navbar />
      <section className="mx-auto max-w-5xl px-6 pt-32 pb-24">
        <Link to="/shadow" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-white/40 hover:text-cyan-300" data-testid="journal-back-link">
          <ArrowLeft className="h-3.5 w-3.5" /> {tFases(lang, "common_back")}
        </Link>

        <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-300/80">// {tFases(lang, "j_h1_eyebrow")}</p>
            <h1 className="mt-2 font-display text-4xl font-black tracking-tight md:text-5xl">{tFases(lang, "j_h1")}</h1>
            <p className="mt-2 max-w-2xl text-sm text-white/55">
              {tFases(lang, "j_h1_sub")}
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-cyan-500 px-5 py-2.5 text-[11px] font-black uppercase tracking-[0.16em] text-black hover:-translate-y-0.5 transition-all"
            data-testid="journal-new-btn"
          >
            <Plus className="h-3.5 w-3.5" /> {tFases(lang, "j_new")}
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="journal-stats">
            <Stat label={tFases(lang, "j_stats_winrate")} value={`${stats.win_rate}%`} accent="emerald" />
            <Stat label={tFases(lang, "j_stats_pnl")} value={`${stats.total_pnl_pct >= 0 ? "+" : ""}${stats.total_pnl_pct}%`} accent={stats.total_pnl_pct >= 0 ? "emerald" : "rose"} />
            <Stat label={tFases(lang, "j_stats_avg")} value={`${stats.avg_pnl_pct >= 0 ? "+" : ""}${stats.avg_pnl_pct}%`} accent="cyan" />
            <Stat label={tFases(lang, "j_stats_closed")} value={stats.closed_count} accent="violet" />
            <Stat label={tFases(lang, "j_stats_open")} value={stats.open_count} accent="amber" />
          </div>
        )}

        {/* Equity curve mini-sparkline */}
        {equityCurve.length > 1 && (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.025] p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">{tFases(lang, "j_equity_title")}</p>
            <Sparkline points={equityCurve} />
          </div>
        )}

        {/* Entries */}
        <div className="mt-8">
          <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-white/55">{tFases(lang, "j_ops", { n: entries.length })}</h2>
          {loading ? (
            <div className="mt-4 flex items-center gap-2 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /> {tFases(lang, "j_loading")}</div>
          ) : entries.length === 0 ? (
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.025] p-10 text-center backdrop-blur">
              <BookText className="mx-auto h-9 w-9 text-white/35" />
              <p className="mt-3 text-sm text-white/55">{tFases(lang, "j_empty")}</p>
            </div>
          ) : (
            <div className="mt-4 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.025] backdrop-blur">
              <table className="w-full text-sm">
                <thead className="border-b border-white/10 bg-white/[0.02]">
                  <tr className="text-left text-[10px] font-mono uppercase tracking-[0.18em] text-white/40">
                    <th className="px-4 py-3">{tFases(lang, "j_th_ticker")}</th>
                    <th className="px-4 py-3">{tFases(lang, "j_th_side")}</th>
                    <th className="px-4 py-3 text-right">{tFases(lang, "j_th_entry")}</th>
                    <th className="px-4 py-3 text-right">{tFases(lang, "j_th_exit")}</th>
                    <th className="px-4 py-3 text-right">{tFases(lang, "j_th_pnl")}</th>
                    <th className="px-4 py-3">{tFases(lang, "j_th_date")}</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => {
                    const side = SIDES.find((s) => s.id === e.side);
                    const open = e.pnl_pct === null || e.pnl_pct === undefined;
                    const pnl = e.pnl_pct;
                    return (
                      <React.Fragment key={e.id}>
                        <tr className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`journal-row-${e.id}`}>
                          <td className="px-4 py-3 font-mono font-bold">{e.ticker}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-wider"
                              style={{ borderColor: side ? `${side.color}66` : "rgba(255,255,255,0.1)", color: side?.color, background: side ? `${side.color}1f` : "transparent" }}>
                              {side && <side.icon className="h-3 w-3" />}
                              {e.side}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-mono">${Number(e.entry_price).toLocaleString()}</td>
                          <td className="px-4 py-3 text-right font-mono">
                            {e.exit_price != null ? `$${Number(e.exit_price).toLocaleString()}` : <span className="text-amber-300/70">{tFases(lang, "j_open")}</span>}
                          </td>
                          <td className="px-4 py-3 text-right font-mono font-bold" style={{ color: pnl == null ? "rgba(255,255,255,0.35)" : pnl >= 0 ? "#34d399" : "#f87171" }}>
                            {pnl == null ? "—" : `${pnl >= 0 ? "+" : ""}${pnl}%`}
                          </td>
                          <td className="px-4 py-3 text-[11px] text-white/45">
                            {e.opened_at ? new Date(e.opened_at).toLocaleDateString() : "—"}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              {open && (
                                <button
                                  onClick={() => { setEditingId(e.id); setEditingExit(""); setEditingLesson(e.lesson || ""); }}
                                  className="rounded-full border border-cyan-400/40 bg-cyan-400/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-cyan-200 hover:bg-cyan-400/20"
                                  data-testid={`journal-close-btn-${e.id}`}
                                >
                                  {tFases(lang, "j_close_btn")}
                                </button>
                              )}
                              <button onClick={() => remove(e.id)} className="rounded p-1 text-white/30 hover:bg-rose-500/10 hover:text-rose-300" data-testid={`journal-delete-${e.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {editingId === e.id && (
                          <tr className="border-t border-cyan-400/15 bg-cyan-400/[0.03]">
                            <td colSpan={7} className="px-4 py-3">
                              <div className="flex flex-col gap-2 md:flex-row md:items-end">
                                <div className="flex-1">
                                  <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/45">{tFases(lang, "j_f_exit")}</label>
                                  <input
                                    type="number" step="any" value={editingExit}
                                    onChange={(ev) => setEditingExit(ev.target.value)}
                                    className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm outline-none focus:border-cyan-400/50"
                                    data-testid={`journal-exit-input-${e.id}`}
                                    placeholder={String(e.entry_price)}
                                  />
                                </div>
                                <div className="flex-1">
                                  <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/45">{tFases(lang, "j_f_lesson")}</label>
                                  <input
                                    value={editingLesson}
                                    onChange={(ev) => setEditingLesson(ev.target.value.slice(0, 500))}
                                    className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm outline-none focus:border-cyan-400/50"
                                    data-testid={`journal-lesson-input-${e.id}`}
                                    placeholder={tFases(lang, "j_f_lesson_ph")}
                                    maxLength={500}
                                  />
                                </div>
                                <div className="flex gap-1">
                                  <button onClick={() => closePosition(e.id)} className="inline-flex h-[38px] items-center gap-1 rounded-lg bg-cyan-400 px-3 text-[10px] font-black uppercase tracking-wider text-black" data-testid={`journal-confirm-close-${e.id}`}>
                                    {tFases(lang, "j_close_btn")}
                                  </button>
                                  <button onClick={() => setEditingId(null)} className="inline-flex h-[38px] items-center gap-1 rounded-lg border border-white/15 bg-white/[0.04] px-3 text-[10px] font-black uppercase tracking-wider text-white/70">
                                    {tFases(lang, "common_cancel")}
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* New entry modal */}
      {showForm && (
        <div className="fixed inset-0 z-[9100] flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-md" onClick={() => setShowForm(false)} data-testid="journal-new-modal">
          <form onSubmit={create} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg overflow-hidden rounded-2xl border border-cyan-400/30 bg-zinc-950 p-6 shadow-[0_0_60px_rgba(34,211,238,0.18)]">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-black">{tFases(lang, "j_close_modal_title")}</h3>
              <button type="button" onClick={() => setShowForm(false)} className="rounded-full border border-white/15 p-1.5 text-white/70 hover:text-white"><XIcon className="h-4 w-4" /></button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Field label={tFases(lang, "j_f_ticker")}>
                <input value={form.ticker} onChange={(e) => setForm({ ...form, ticker: e.target.value.toUpperCase().slice(0, 20) })} className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm outline-none focus:border-cyan-400/50" data-testid="journal-form-ticker" />
              </Field>
              <Field label={tFases(lang, "j_f_side")}>
                <div className="flex gap-1">
                  {SIDES.map((s) => (
                    <button type="button" key={s.id} onClick={() => setForm({ ...form, side: s.id })} data-testid={`journal-form-side-${s.id}`}
                      className="flex-1 rounded-lg border px-2 py-2 text-[10px] font-black uppercase tracking-wider"
                      style={{ borderColor: form.side === s.id ? s.color : "rgba(255,255,255,0.1)", background: form.side === s.id ? `${s.color}1f` : "transparent", color: form.side === s.id ? s.color : "rgba(255,255,255,0.55)" }}>
                      {s.id}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label={tFases(lang, "j_f_entry")}>
                <input type="number" step="any" value={form.entry_price} onChange={(e) => setForm({ ...form, entry_price: e.target.value })} required className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm outline-none focus:border-cyan-400/50" data-testid="journal-form-entry" />
              </Field>
              <Field label={tFases(lang, "j_f_exit")}>
                <input type="number" step="any" value={form.exit_price} onChange={(e) => setForm({ ...form, exit_price: e.target.value })} className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm outline-none focus:border-cyan-400/50" data-testid="journal-form-exit" />
              </Field>
              <Field label={tFases(lang, "j_f_size")}>
                <input type="number" step="any" value={form.position_size_usd} onChange={(e) => setForm({ ...form, position_size_usd: e.target.value })} className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm outline-none focus:border-cyan-400/50" data-testid="journal-form-size" />
              </Field>
            </div>
            <Field label={tFases(lang, "j_f_notes")}>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value.slice(0, 2000) })} rows={3} maxLength={2000} placeholder={tFases(lang, "j_f_notes_ph")} className="w-full resize-none rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm outline-none focus:border-cyan-400/50" data-testid="journal-form-notes" />
            </Field>
            {error && <p className="mt-2 text-xs text-rose-300" data-testid="journal-form-error">{error}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="rounded-full border border-white/15 px-4 py-2 text-[11px] font-black uppercase tracking-wider text-white/70">{tFases(lang, "common_cancel")}</button>
              <button type="submit" disabled={creating} className="inline-flex items-center gap-1.5 rounded-full bg-cyan-400 px-5 py-2 text-[11px] font-black uppercase tracking-wider text-black disabled:opacity-60" data-testid="journal-form-submit">
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                {tFases(lang, "j_f_save")}
              </button>
            </div>
          </form>
        </div>
      )}

      <Footer />
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="mt-3">
    <label className="block text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const Stat = ({ label, value, accent }) => {
  const palettes = {
    emerald: "border-emerald-400/30 bg-emerald-400/[0.06] text-emerald-200",
    cyan: "border-cyan-400/30 bg-cyan-400/[0.06] text-cyan-200",
    violet: "border-violet-400/30 bg-violet-400/[0.06] text-violet-200",
    amber: "border-amber-400/30 bg-amber-400/[0.06] text-amber-200",
    rose: "border-rose-400/30 bg-rose-400/[0.06] text-rose-200",
  };
  return (
    <div className={`relative overflow-hidden rounded-2xl border ${palettes[accent] || palettes.cyan} p-4 backdrop-blur`}>
      <p className="font-mono text-2xl font-black">{value}</p>
      <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.18em] text-white/45">{label}</p>
    </div>
  );
};

const Sparkline = ({ points }) => {
  const w = 800, h = 64;
  const min = Math.min(0, ...points);
  const max = Math.max(0, ...points);
  const range = max - min || 1;
  const path = points.map((p, i) => {
    const x = (i / Math.max(points.length - 1, 1)) * w;
    const y = h - ((p - min) / range) * h;
    return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(" ");
  const isUp = points[points.length - 1] >= 0;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="mt-2 h-16 w-full" data-testid="journal-equity-svg">
      <path d={path} fill="none" stroke={isUp ? "#34d399" : "#f87171"} strokeWidth="2" />
    </svg>
  );
};
